/**
 * ResendInviteModal
 *
 * Confirmation modal for resending an expired invite.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/organisms/ResendInviteModal/`
 *
 * ## Features
 * - Shows confirmation message with invite email
 * - Calls resend API on confirmation
 * - Updates the invite in query cache with the refreshed data
 * - Shows error/success messages
 *
 * @module features/users/components/ui/organisms/ResendInviteModal
 */
import React, { useCallback } from 'react';
import { App, Modal, Typography } from 'antd';
import { Button } from '@/shared/components/ui/atoms';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { InviteItem } from '@/features/users/types/users.types';
import { resendUserInvite } from '@/features/users/services/userInvites';
import { USER_INVITES_QUERY_KEY } from '@/features/users/hooks/useUserInvites';

import './ResendInviteModal.css';

export interface ResendInviteModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** The invite to resend */
  invite: InviteItem | null;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when resend is successful */
  onSuccess?: () => void;
}

/**
 * ResendInviteModal Component
 *
 * @param props - Component properties
 * @returns React component
 *
 * @example
 * ```tsx
 * <ResendInviteModal
 *   open={isOpen}
 *   invite={selectedInvite}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => refetchInvites()}
 * />
 * ```
 */
export const ResendInviteModal: React.FC<ResendInviteModalProps> = ({
  open,
  invite,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const { mutateAsync: doResend, isPending } = useMutation({
    mutationFn: (inv: Pick<InviteItem, 'id' | 'email' | 'fullName'>) =>
      resendUserInvite(inv),
    onSuccess: () => {
      // The old invite was deleted and a new one created — refetch the list
      queryClient.invalidateQueries({ queryKey: USER_INVITES_QUERY_KEY });
    },
  });

  const handleConfirm = useCallback(async () => {
    if (!invite) return;

    try {
      await doResend({
        id: invite.id,
        email: invite.email,
        fullName: invite.fullName,
      });
      notification.success({
        message: t('Invite resent'),
        placement: 'topRight',
      });
      onSuccess?.();
      onClose();
    } catch {
      notification.error({
        message: t('Failed to resend invite'),
        placement: 'topRight',
      });
    }
  }, [invite, doResend, notification, t, onSuccess, onClose]);

  const inviteEmail = invite?.email ?? '';

  return (
    <Modal
      open={open}
      width={320}
      title={t('Resend invite')}
      onCancel={onClose}
      footer={
        <div className="resend-invite-modal__footer">
          <Button variant="ghost" size="lg" fullWidth={false} onClick={onClose}>
            {t('Close')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            fullWidth={false}
            loading={isPending}
            onClick={handleConfirm}
          >
            {t('Resend invite')}
          </Button>
        </div>
      }
      destroyOnHidden
      className="resend-invite-modal"
      aria-label={t('Resend invite confirmation modal')}
    >
      <div className="resend-invite-modal__content">
        <Typography.Text className="resend-invite-modal__question">
          {t('Are you sure you want to resend the invitation to the')}
          <strong> {inviteEmail}</strong>
        </Typography.Text>
      </div>
    </Modal>
  );
};
